import React, { useEffect, useState } from 'react';
import { AddTwoToneIcon, Anchor, Button, KebabMenu, PageHeader, Pager, Panel, SearchBar, Table, TableColumn, TableRow } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { SearchSkillsRequest } from '../../../models/overmind/search-params';
import { useAppActions, useAppState } from '../../../overmind';
import { CompanySkillListItemResponse, CompanySkillListResponse } from '../../../models/overmind/company-entity';
import { SaveSkillPanel } from './save-skill/save-skill.panel';
import { PagePath } from '../../../navigation/navigation.enums';
import roleSkillManagementStyles from '../role-skill-management.module.css';
import classNames from 'classnames';

export function ManageSkillsPage(): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [request, setRequest] = useState<SearchSkillsRequest>({
    search: undefined,
    sortAscending: true,
    pageSize: 10,
    page: 1,
    companySlug: companyVariables.slug,
    searchGlobally: false,
  });
  const [data, setData] = useState<CompanySkillListResponse>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedSkill, setSelectedSkill] = useState<CompanySkillListItemResponse | undefined>();

  async function getData(request: SearchSkillsRequest) {
    const response = await actions.getCompanySkills(request);
    setData(response);
  }

  useEffect(() => {
    async function initPage() {
      setIsLoading(true);
      await getData(request);
      setIsLoading(false);
    }

    initPage();
  }, [companyVariables.slug]);

  function onClosePanel(): void {
    setIsOpen(false);
    setSelectedSkill(undefined);
  }

  return (
    <React.Fragment>
      <div className="wrapper stack" style={{ gap: 24 }}>
        <PageHeader
          breadcrumbs={[
            { name: 'Roles & skills', url: PagePath.roleSkillManagementBase },
            { name: 'Manage skills', url: window.location.pathname },
          ]}
          title="Manage skills"
          subtitle="A library of all the skills within your company"
        />

        <div className={roleSkillManagementStyles.actions}>
          <SearchBar
            loading={isSearching}
            label="Search"
            onInput={async event => {
              setIsSearching(true);
              const search = (event.target as HTMLInputElement).value;
              const nextRequest = { ...request, search, page: 1 };
              setRequest(nextRequest);
              await getData(nextRequest);
              setIsSearching(false);
            }}
          />

          <Button type="button" square theme="dark" onClick={() => setIsOpen(true)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <AddTwoToneIcon />
              Create skill
            </div>
          </Button>
        </div>

        {isLoading ? (
          <SkeletonLoader height="500px" />
        ) : !data?.skills.length ? (
          <EmptyState />
        ) : (
          <Table
            standAloneConfigColumn
            currentSortBy="skill"
            currentSortDirection={request.sortAscending ? 'Ascending' : 'Descending'}
            onSort={() => setRequest(currentState => ({ ...currentState, sortAscending: !currentState.sortAscending }))}
            footerContent={
              <div className={classNames({ invisible: data.totalPages <= 1 })}>
                <PagerConnector
                  defaultPageNumber={request.page}
                  pageCount={data?.totalPages ?? 0}
                  onPageChange={async page => {
                    setIsLoading(true);
                    const nextRequest = { ...request, page };
                    setRequest(nextRequest);
                    await getData(nextRequest);
                    setIsLoading(false);
                  }}
                >
                  {connector => <Pager {...connector} isLoading={isLoading} />}
                </PagerConnector>
              </div>
            }
          >
            {data?.skills.map(skill => (
              <TableRow
                key={skill.slug}
                id={skill.slug!}
                configCell={() => (
                  <KebabMenu
                    items={[
                      {
                        label: 'Edit',
                        onClick: () => setSelectedSkill(skill),
                      },
                    ]}
                  />
                )}
              >
                <TableColumn sortable id="skill" label="Skill">
                  <Anchor hovertype="opacity" textTransform="none" onClick={() => setSelectedSkill(skill)}>
                    {skill.name}
                  </Anchor>
                </TableColumn>

                <TableColumn id="description" label="Description" emptyText="No description provided">
                  <div className={roleSkillManagementStyles.description}>{skill.description}</div>
                </TableColumn>
              </TableRow>
            ))}
          </Table>
        )}
      </div>

      <Panel open={isOpen || !!selectedSkill} onClose={onClosePanel}>
        {(isOpen || !!selectedSkill) && (
          <SaveSkillPanel
            selectedSkill={selectedSkill}
            onClose={onClosePanel}
            refreshData={async () => {
              setIsLoading(true);
              await getData(request);
              setIsLoading(false);
            }}
          />
        )}
      </Panel>
    </React.Fragment>
  );
}
